import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import BookSkeleton from '@/components/feature/BookSkeleton';
import EmptyState from '@/components/base/EmptyState';
import { books, categories, siteConfig } from '@/mocks/data';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首頁', item: 'https://lelyric.sonbook.net/' },
    { '@type': 'ListItem', position: 2, name: '書籍', item: 'https://lelyric.sonbook.net/books' },
  ],
};

export default function Books() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const hasBooks = books.length > 0;
  const hasCategories = categories.length > 0;
  const filteredBooks = books.filter((b) => {
    const matchSearch = !search || b.title.includes(search) || b.author.includes(search);
    const matchCategory = !categoryFilter || b.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  return (
    <div className="min-h-screen bg-background-50">
      <PageMeta
        title="樂律出版書籍｜樂律 Lelyric"
        description="瀏覽樂律正式出版書籍，並前往崧博出版書籍資料庫查詢完整書名、作者、ISBN與出版資訊。"
        canonicalPath="/books"
        structuredData={breadcrumbSchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[300px] md:min-h-[420px] flex items-center bg-foreground-950 overflow-hidden">
          {/* Full-bleed background image */}
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20composition%20inspired%20by%20the%20quiet%20rhythm%20of%20bookshelves%20and%20stacked%20volumes%2C%20overlapping%20rectangular%20forms%20and%20layered%20translucent%20shapes%20in%20warm%20cream%20ivory%20and%20deep%20muted%20indigo%20tones%2C%20soft%20ambient%20light%20creating%20gentle%20diffuse%20shadows%20across%20the%20scene%2C%20editorial%20minimalism%20with%20subtle%20paper%20textures%20and%20fine%20grain%2C%20serene%20contemplative%20mood%20evoking%20a%20calm%20library%20or%20reading%20room%2C%20no%20people%2C%20no%20text%2C%20painterly%20modern%20digital%20art%20aesthetic%2C%20dark%20navy%20and%20warm%20neutral%20palette%2C%20publishing%20house%20brand%20visual%20identity%20with%20understated%20elegance%20and%20generous%20breathing%20negative%20space&width=1600&height=800&seq=lelyric-books-hero-bg&orientation=landscape"
              alt=""
              width={1600}
              height={800}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/80"></div>

          {/* Fine horizontal rules */}
          <div className="absolute top-0 left-0 right-0 h-px bg-background-50/6"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-background-50/6"></div>

          {/* Page number */}
          <div className="absolute top-6 right-6 md:top-10 md:right-14 select-none pointer-events-none">
            <span className="font-heading text-5xl md:text-7xl font-bold text-background-50/[0.03]">03</span>
          </div>

          {/* Content */}
          <div className="relative z-10 w-full px-6 md:px-10 py-16 md:py-20">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[{ label: '首頁', href: '/' }, { label: '書籍' }]}
                light
              />
              <div className="flex items-center gap-2.5 mb-6">
                <span className="block w-5 h-px bg-primary-400/40"></span>
                <span className="text-[13px] text-primary-400/70 tracking-[0.15em] uppercase font-medium">Books</span>
              </div>
              <h1 className="font-heading text-[3rem] md:text-[4rem] font-bold text-background-50 leading-[1.12] tracking-tight mb-4">
                樂律出版書籍
              </h1>
              <p className="text-base md:text-lg text-background-50/50 max-w-xl leading-relaxed font-light tracking-wide">
                瀏覽樂律正式出版書籍，或前往崧博出版書籍資料庫查詢完整書目與出版資訊。
              </p>
            </div>
          </div>

          {/* Bottom gradient transition */}
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background-50/20 to-transparent"></div>
        </section>

        {/* Books Content */}
        <section className="px-6 md:px-10 py-16 md:py-24 bg-background-50">
          <div className="max-w-6xl mx-auto">
            {!hasBooks ? (
              <div className="py-10">
                <EmptyState
                  icon="ri-book-open-line"
                  title="正式書籍資料待匯入"
                  description="目前尚無正式書籍資料，請前往崧博出版書籍資料庫查詢完整書目資訊。"
                  actions={[
                    { label: '前往書籍資料庫', path: siteConfig.catalogUrl, external: true, primary: true },
                    { label: '返回首頁', path: '/' },
                    { label: '聯絡樂律', path: '/contact' },
                  ]}
                />
              </div>
            ) : (
              <>
                {/* Filters */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-12">
                  <div className="relative flex-1 w-full sm:max-w-sm">
                    <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="搜尋書名或作者..."
                      aria-label="搜尋書名或作者"
                      className="w-full pl-10 pr-4 py-2.5 rounded-full bg-background-100 border border-foreground-200/60 text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400/40 focus:ring-1 focus:ring-primary-400/20 transition-colors"
                    />
                  </div>
                  {hasCategories && (
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      aria-label="依分類篩選"
                      className="px-4 py-2.5 rounded-full bg-background-100 border border-foreground-200/60 text-base text-foreground-900 focus:outline-none focus:border-primary-400/40 cursor-pointer appearance-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666666' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 1rem center',
                        paddingRight: '2.5rem',
                      }}
                    >
                      <option value="">全部分類</option>
                      {categories.map((c) => (
                        <option key={c.slug} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Books Grid */}
                {filteredBooks.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-10 gap-y-14">
                    {filteredBooks.map((book) => (
                      <a
                        key={book.slug}
                        href={`/books/${book.slug}`}
                        className="group block cursor-pointer"
                        aria-label={`查看書籍：${book.title}`}
                      >
                        <div
                          className="aspect-[3/4] w-full rounded-sm mb-5 relative overflow-hidden transition-all duration-300 group-hover:translate-y-[-3px]"
                          role="img"
                          aria-label={`${book.title} 書封`}
                        >
                          <img
                            src={book.coverImage}
                            alt={book.title}
                            width={300}
                            height={400}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                          {book.title}
                        </h3>
                        <p className="text-[13px] text-foreground-500 mt-1">{book.author}</p>
                      </a>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon="ri-search-line"
                    title="找不到符合條件的書籍"
                    description="請嘗試其他搜尋關鍵字或篩選條件。"
                  />
                )}
              </>
            )}
          </div>
        </section>

        {/* Catalog CTA when no books */}
        {!hasBooks && (
          <section className="px-6 md:px-10 pb-20 md:pb-28 bg-background-50">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="font-heading text-[2.25rem] md:text-[2.75rem] font-semibold text-foreground-950 mb-4 leading-[1.2] tracking-tight">
                查詢完整書目
              </h2>
              <p className="text-base text-foreground-500 leading-relaxed max-w-md mx-auto mb-9 font-light">
                正式書名、作者、ISBN、出版日期等資訊，請前往崧博出版書籍資料庫查詢。
              </p>
              <a
                href={siteConfig.catalogUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-[15px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer tracking-wide"
              >
                前往書籍資料庫
                <i className="ri-external-link-line text-[13px]"></i>
              </a>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}